import React from 'react';
import { useTopicStore } from './store';
import ThinkingIndicator from '../../components/ThinkingIndicator';
import type { Topic, WikiTopic } from './types/detailsCard.types';

interface HistoricalPeriodsV2Props {
  topicId?: string | null;
  topic: Topic | null;
  userLanguageLabel: string;
  isGuestAccount?: boolean;
  onSignInClick?: () => void;
  startNarration?: (...args: any[]) => void;
  locationData?: any;
}

export default function HistoricalPeriodsV2({
  topicId,
  topic,
  userLanguageLabel,
  isGuestAccount = true,
  onSignInClick = () => {},
  startNarration = () => {},
  locationData,
}: HistoricalPeriodsV2Props) {
  const fetchHistoricalPeriods = useTopicStore((s) => s.fetchHistoricalPeriods);
  const periodsState = useTopicStore((s) => {
    if (!topicId) return null;
    return s.topics[topicId]?.historicalPeriods ?? null;
  });

  const hasCurrentLanguageState = periodsState?.languageLabel === userLanguageLabel;
  const items = hasCurrentLanguageState ? (periodsState?.items ?? []) : [];
  const isLoading = hasCurrentLanguageState && !!periodsState?.isLoading;
  const error = hasCurrentLanguageState ? (periodsState?.error ?? null) : null;

  if (!topicId || !topic) return null;

  const runHistoricalResearch = () => {
    if (!topicId) return;
    void fetchHistoricalPeriods(topicId, { userLanguageLabel });
  };

  if (isLoading && items.length === 0) {
    return (
      <ThinkingIndicator />
    );
  }

  if (error && items.length === 0) {
    const needsAuth = error === 'AUTH_REQUIRED' && isGuestAccount;

    if (needsAuth) {
      return (
        <div style={{ padding: '15px 10px', textAlign: 'center' }}>
          <button
            onClick={onSignInClick}
            style={{
              backgroundColor: '#22c55e',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              width: '100%',
            }}
          >
            Sign in to learn more
          </button>
        </div>
      );
    }

    return (
      <div style={{ padding: '10px', textAlign: 'left' }}>
        <div style={{ color: '#666', marginBottom: '10px' }}>{error}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <button
            onClick={runHistoricalResearch}
            className="period-btn"
            style={{ width: '100%', textAlign: 'left' }}
          >
            Research
          </button>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div style={{ padding: '10px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <button
            onClick={runHistoricalResearch}
            className="period-btn"
            style={{ width: '100%', textAlign: 'left' }}
          >
            Research
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {items.map((p) => (
        <button
          key={`${p.title}-${p.date}`}
          className="period-btn"
          onClick={() => {
            const currentArticle = topic.type === 'wiki' ? (topic as WikiTopic) : null;
            startNarration(
              topic.name,
              'historicPeriod',
              { period: `${p.title} (${p.date})` },
              locationData,
              currentArticle,
            );
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <div>
              <strong>{p.title}</strong>
              <span className="period-date">{p.date}</span>
            </div>
            <i className="fas fa-volume-up" style={{ color: '#6b7280', fontSize: '1rem', opacity: 0.5 }} />
          </div>
        </button>
      ))}
    </>
  );
}
